import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import i18n from "@/i18n";

import EmailEditor from "@/components/emailEditor";
import UserCommRecipientList from "@/components/userComms/userCommRecipientList";
import UserCommSendPanel from "@/components/userComms/userCommSendPanel";
import UserCommReplyToSelect from "@/components/userComms/userCommReplyToSelect";
import { Input } from "@/components/ui/input";
import useUserComm from "@/hooks/notifications/useUserComm";
import useUpdateUserComm from "@/hooks/notifications/useUpdateUserComm";
import useDuplicateUserComm from "@/hooks/notifications/useDuplicateUserComm";
import useDeleteUserComm from "@/hooks/notifications/useDeleteUserComm";
import LoadingPanel from "../loadingPanel";
import DialogModal from "../dialogModal";
import { Button } from "@/components/ui/button";
import { Filter, Mails, Trash2 } from "lucide-react";
import useDialogModal from "@/hooks/useDialogModal";
import useLatestShareData from "@/hooks/shares/useLatestShareData";
import { Badge } from "../ui/badge";
import { getPlaceholderFieldsConfig } from "../tiptap-editor/extensions/placeholder";
import { Trans } from "react-i18next";
import AttachmentsByObjidContainer from "@/containers/shared/attachments-by-objid.container";
import { renderCommonAttachmentDropdown } from "@/containers/documents/attachments/predefined-dropdown-render-functions";
import { ValidationArea } from "@/hocs/withValidation";
import { useSelector } from "react-redux";
import useValidation from "@/hooks/useValidation";
import LastSavedIndicator from "../lastSavedIndicator";
import useMe from "../../hooks/users/useMe";

const { t } = i18n;

export const UserCommStatusBadge = ({ status, ...props }) => {
  return (
    <Badge
      variant={
        { draft: "outline-orange", sent: "outline-success" }[status] ??
        "outline"
      }
      {...props}
    >
      {status ? t(`user_comms.status.${status}`) : "..."}
    </Badge>
  );
};

const UserCommsEdit = ({
  id,
  onCancel,
  onChangeSelectedUserComm,
  onSetFilter,
}) => {
  const { dialogElement, alert, confirm } = useDialogModal();

  const [editor, setEditor] = useState();

  const {
    data: latestShareDataQuery,
    isLoading: isLoadingLatestShareData,
    error: latestShareDataError,
  } = useLatestShareData();
  const latestShareData = latestShareDataQuery?.data;
  const shareTypes = latestShareData?.types
    ?.map((t) => t.shareType)
    .filter(Boolean);

  const { data: userCommQuery, isLoading: isLoadingUserComm } = useUserComm(id);
  const userComm = userCommQuery?.data;

  const { mutateAsync: updateUserComm } = useUpdateUserComm(id);
  const { mutateAsync: duplicateUserComm, isPending: isDuplicatingUserComm } =
    useDuplicateUserComm(id);
  const { mutateAsync: deleteUserComm, isPending: isDeletingUserComm } =
    useDeleteUserComm(id);
  const isPending = isDuplicatingUserComm || isDeletingUserComm;

  const { data: useMeQuery } = useMe();
  const currentUser = useMeQuery?.data;
  const currentUserId = currentUser?.id;

  const [subject, setSubject] = useState();
  const [showSendPanel, setShowSendPanel] = useState(false);

  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  const attachments = useSelector((state) =>
    state.attachments.getIn(["attachments", id])?.toJS(),
  );

  const { validate, validationMessages } = useValidation({
    attachments: (assert) => {
      if (!attachments?.length) return;

      const attachmentsSize = attachments?.reduce(
        (acc, attachment) => acc + attachment?.file?.size,
        0,
      );

      assert(
        attachmentsSize <= 1024 * 1024 * 25,
        t("user_comms.validation.attachments_too_large"),
      );
    },
  });

  useEffect(() => {
    validate();
  }, attachments?.length);

  const isLocked =
    userComm?.status !== "draft" || userComm?.userId !== currentUserId;

  const isSubmitDisabled =
    isLocked || isEditorEmpty || !userComm?.recipients?.length;

  const saveDraft = useCallback(
    debounce((payload) => {
      updateUserComm(payload);
    }, 1000),
    [],
  );

  useEffect(() => {
    if (subject) saveDraft({ subject });
  }, [subject]);

  const isLoading = !userComm || isLoadingLatestShareData;

  return (
    <>
      {latestShareDataError?.response?.status === 401 ? (
        <DialogModal
          variant="destructive"
          title={t("generic.permissions_missing")}
          bodyRenderer={() => (
            <Trans i18nKey="user_comms.permissions_missing.description" />
          )}
          submitText={null}
          onCancel={onCancel}
        />
      ) : (
        <DialogModal
          open
          size="xl"
          title={
            <div className="flex items-center gap-2">
              {t("user_comms.edit_user_comm.title")}{" "}
              <UserCommStatusBadge status={userComm?.status} />
            </div>
          }
          onCancel={onCancel}
          cancelDisabled={isPending}
          cancelText={t("generic.form.close")}
          submitText={isLocked ? null : t("generic.send")}
          submitDisabled={isSubmitDisabled}
          onSubmit={() => setShowSendPanel(true)}
          footerRenderer={(props) => {
            if (isLoading) return null;

            const { cancelButtonRenderer, submitButtonRenderer } = props;

            return (
              <div className="flex w-full justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    disabled={isLoadingUserComm}
                    isLoading={isDeletingUserComm}
                    onClick={async () => {
                      const confirmDelete = await confirm(
                        t("user_comms.actions.delete_user_comm.title"),
                        t("user_comms.actions.delete_user_comm.body"),
                        {
                          submitText: t(
                            "user_comms.actions.delete_user_comm.title",
                          ),
                          variant: "destructive",
                        },
                      );

                      if (confirmDelete) {
                        await deleteUserComm();
                        onCancel();
                      }
                    }}
                  >
                    <Trash2 size={16} />{" "}
                    {t("user_comms.actions.delete_user_comm.title")}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={isLoadingUserComm}
                    isLoading={isDuplicatingUserComm}
                    onClick={async () => {
                      const newUserComm = await duplicateUserComm();
                      onChangeSelectedUserComm?.(newUserComm.data.id);
                      await alert(
                        t("user_comms.actions.duplicate_user_comm.title"),
                        t("user_comms.actions.duplicate_user_comm.success"),
                        { variant: "success" },
                      );
                    }}
                  >
                    <Mails size={16} />
                    {t("user_comms.actions.duplicate_user_comm.title")}
                  </Button>
                  {userComm?.status === "sent" && (
                    <Button
                      variant="outline"
                      disabled={isLoadingUserComm}
                      onClick={() => {
                        onCancel();
                        onSetFilter?.(userComm.id);
                      }}
                    >
                      <Filter size={16} />
                      {t("user_comms.actions.filter_by_this.title")}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <LastSavedIndicator value={userComm?.updatedAt} />
                  {cancelButtonRenderer?.(props, null)}
                  {submitButtonRenderer?.(props, null)}
                </div>
              </div>
            );
          }}
          bodyRenderer={() =>
            isLoading ? (
              <LoadingPanel />
            ) : (
              <div className="grid grid-cols-[1fr_300px] gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex w-full gap-4">
                    <Input
                      label={t("subject")}
                      hint={t("user_comms.subject.hint")}
                      readOnly={isLocked}
                      autoFocus
                      value={subject ?? userComm.subject}
                      onChange={(evt) => setSubject(evt.target.value)}
                    />
                    <UserCommReplyToSelect
                      disabled={isLocked}
                      value={userComm.replyToEmail ?? "no-reply@invono.se"}
                      onChange={(value) =>
                        updateUserComm({ replyToEmail: value })
                      }
                    />
                  </div>
                  <EmailEditor
                    readOnly={isLocked}
                    onCreate={({ editor }) => {
                      setEditor(editor);
                      editor.commands.setContent(userComm.body);
                      setIsEditorEmpty(editor.isEmpty);
                    }}
                    onUpdate={({ editor }) => {
                      setIsEditorEmpty(editor.isEmpty);
                      saveDraft({ body: editor.getJSON() });
                    }}
                    placeholderFields={getPlaceholderFieldsConfig({
                      shareTypes,
                    })}
                  />
                  <ValidationArea
                    validationMessage={validationMessages.attachments}
                  >
                    <AttachmentsByObjidContainer
                      objType="userComm"
                      objId={id}
                      renderAttachmentDropdown={renderCommonAttachmentDropdown}
                      readOnly={isLocked}
                      isDragDisabled
                      nakedStyle
                    />
                  </ValidationArea>
                </div>
                <UserCommRecipientList
                  editor={editor}
                  userCommId={id}
                  disabled={isLocked}
                />
                {showSendPanel && (
                  <UserCommSendPanel
                    userCommId={id}
                    editor={editor}
                    onCancel={() => setShowSendPanel(false)}
                    onComplete={() => {
                      onCancel();
                      onSetFilter?.(userComm.id);
                    }}
                  />
                )}
                {dialogElement}
              </div>
            )
          }
        />
      )}
    </>
  );
};

export default UserCommsEdit;
